// src/AgentsPage.js
import React, { useMemo, useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  LinearProgress,
  alpha,
  Divider,
  Slider,
  Chip,
  Paper,
  Stack,
  Badge,
  Tooltip as MuiTooltip
} from '@mui/material';
import { ShieldCheck, TrendingUp, TrendingDown, Award, Info, Settings, Target, BarChart3 } from 'lucide-react';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';

// --- Mock Data for Agents ---
const agentData = [
  {
    id: 1,
    name: 'Priya Sharma',
    avatarInitial: 'PS',
    avatarColor: 'primary.main',
    scores: {
      credit_reliability: 82,
      customer_loyalty_score: 85,
      accident_risk: 25, // Lower is better
      claim_severity: 55, // Lower is better
      coverage_adequacy_score: 90,
      claim_ratio: 30, // Lower is better,
      grievance_ratio: 36,
      agent_retention: 81,
      ROI_per_customer: 756,
    },
  },
  {
    id: 2,
    name: 'Rohan Verma',
    avatarInitial: 'RV',
    avatarColor: 'secondary.main',
    scores: {
      credit_reliability: 68,
      customer_loyalty_score: 55,
      accident_risk: 45, // Lower is better
      claim_severity: 50, // Lower is better
      coverage_adequacy_score: 70,
      claim_ratio: 60, // Lower is better
      grievance_ratio: 45,
      agent_retention: 57,
      ROI_per_customer: 660,
    },
  },
];

// --- Helper Functions (adapted from ScoreCircularProgress) ---

const toTitleCase = (str) =>
  str
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// Scores where a lower value is better
const invertColors = ['claim_severity', 'accident_risk', 'claim_ratio', 'grievance_ratio'];

const getColor = (value, name) => {
  const colors = {
    high: '#4C84FF', // Blue (Excellent)
    mid: '#FF9F43',  // Orange/Amber (Good/Fair)
    low: '#FF5C75',   // Red (Poor)
  };

  if (invertColors.includes(name)) {
    // Inverted logic: lower score is better (blue)
    if (value < 40) return colors.high;
    if (value < 60) return colors.mid;
    return colors.low;
  } else {
    // Normal logic: higher score is better (blue)
    if (value < 40) return colors.low;
    if (value < 60) return colors.mid;
    return colors.high;
  }
};

// --- Component ---

const AgentsPage = () => {
  // Parameter definitions (default, can be updated later)
  const paramDefinitions = useMemo(() => ({
    credit_reliability: 'Measures the credit worthiness and reliability in repayments.',
    customer_loyalty_score: 'Indicates repeat business and satisfaction trend among customers.',
    accident_risk: 'Estimated risk of customers filing accident-related claims (lower is better).',
    claim_severity: 'Average claim severity indicating payout magnitude (lower is better).',
    coverage_adequacy_score: 'How well products sold match customer coverage needs.',
    claim_ratio: 'Claims incurred vs premium collected (lower is better).',
    grievance_ratio: 'Frequency of grievances per policy/customer (lower is better).',
    agent_retention: 'Agent\'s ability to retain clients across renewals.',
    ROI_per_customer: 'Average return generated per customer in rupees.',
  }), []);

  // Configurable weights for agent metrics, 0–1 sliders similar to individual scoring
  const [weights, setWeights] = useState({});

  // Initialize weights to 1.0 for all available metrics
  useEffect(() => {
    const keys = Object.keys(agentData?.[0]?.scores || {});
    const initial = {};
    keys.forEach(k => { initial[k] = 1.0; });
    setWeights(initial);
  }, []);

  // Using raw 0–1 weights directly (no normalization of sum)

  // ROI normalization function: converts Rs value to 0-100 by dividing by 10 and clamping, then rounding
  const normalizeRoiToScore = (roiInRupees) => {
    const score = Math.round(Math.min(100, Math.max(0, roiInRupees / 10)));
    return score;
  };

  // Compute final weighted score for an agent using all parameters
  const computeFinalAgentScore = (agent) => {
    let sum = 0;
    let maxPossible = 0;
    const contributions = [];
    Object.entries(agent.scores).forEach(([key, raw]) => {
      let normalizedVal = raw;
      if (key === 'ROI_per_customer') {
        normalizedVal = normalizeRoiToScore(raw);
      } else if (invertColors.includes(key)) {
        normalizedVal = 100 - Math.min(100, Math.max(0, raw));
      } else {
        normalizedVal = Math.min(100, Math.max(0, raw));
      }
      const w = weights[key] ?? 0;
      sum += normalizedVal * w;
      maxPossible += 100 * w;
      contributions.push({ key, normalizedVal, weight: w, product: normalizedVal * w });
    });
    const finalScore = Math.round((sum / maxPossible) * 100);
    return { finalScore, contributions, maxPossible };
  };

  const handleWeightChange = (key, value) => {
    setWeights(prev => ({ ...prev, [key]: value }));
  };

  // Final score breakdown popover state
  const [breakdownAnchor, setBreakdownAnchor] = useState(null);
  const [breakdownAgentId, setBreakdownAgentId] = useState(null);
  const openBreakdown = Boolean(breakdownAnchor);
  const handleOpenBreakdown = (event, agentId) => {
    setBreakdownAgentId(agentId);
    setBreakdownAnchor(event.currentTarget);
  };
  const handleCloseBreakdown = () => {
    setBreakdownAnchor(null);
    setBreakdownAgentId(null);
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Enhanced Header */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          borderRadius: 4,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
              <BarChart3 size={28} />
            </Avatar>
            <Box>
              <Typography variant="h3" fontWeight="800" gutterBottom>
                Agent Performance Analysis
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                AI-powered performance insights with customizable scoring weights
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              icon={<Target size={16} />}
              label="Real-time Analytics"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
            />
            <Chip
              icon={<Settings size={16} />}
              label="Customizable Weights"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
            />
            <Chip
              icon={<Award size={16} />}
              label="Performance Scoring"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
            />
          </Box>
        </Box>
        
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            zIndex: 1
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            zIndex: 1
          }}
        />
      </Paper>

      <Grid container spacing={4}>
        {agentData.map((agent) => (
          <Grid item xs={12} lg={6} key={agent.id}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
                height: '100%',
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 25px 70px rgba(0,0,0,0.15)'
                }
              }}
            >
              {/* Agent Header */}
              <Box
                sx={{
                  p: 3,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', zIndex: 2 }}>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 64, height: 64, mr: 3, fontSize: '1.8rem', fontWeight: 'bold' }}>
                    {agent.avatarInitial}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="700">{agent.name}</Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>Senior Sales Agent</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Chip size="small" label="Active" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                      <Chip size="small" label="Top Performer" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                    </Box>
                  </Box>
                </Box>
                
                {/* Background decoration */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)',
                    zIndex: 1
                  }}
                />
              </Box>
              
              <CardContent sx={{ p: 4 }}>
                {/* Final Agent Score */}
                {(() => {
                  const { finalScore } = computeFinalAgentScore(agent);
                  const scoreColor = finalScore >= 75 ? '#4C84FF' : finalScore >= 60 ? '#FF9F43' : '#FF5C75';
                  const scoreLabel = finalScore >= 75 ? 'Excellent' : finalScore >= 60 ? 'Good' : 'Poor';
                  const scoreIcon = finalScore >= 75 ? <TrendingUp size={18} /> : finalScore >= 60 ? <Award size={18} /> : <TrendingDown size={18} />;
                  
                  return (
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        mb: 3, 
                        border: '2px solid', 
                        borderColor: alpha(scoreColor, 0.3), 
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${alpha(scoreColor, 0.05)} 0%, ${alpha(scoreColor, 0.02)} 100%)`,
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      <Box sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ bgcolor: scoreColor, color: 'white', mr: 2, width: 48, height: 48 }}>
                              {scoreIcon}
                            </Avatar>
                            <Box>
                              <Typography variant="h4" fontWeight="800" sx={{ color: scoreColor, lineHeight: 1 }}>
                                {finalScore} / 100
                              </Typography>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                OVERALL SCORE
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MuiTooltip title="Show score breakdown">
                              <IconButton size="small" onClick={(e) => handleOpenBreakdown(e, agent.id)}>
                                <Info size={16} />
                              </IconButton>
                            </MuiTooltip>
                            <Chip 
                              icon={scoreIcon}
                              size="medium" 
                              label={scoreLabel} 
                              sx={{ 
                                bgcolor: alpha(scoreColor, 0.15), 
                                color: scoreColor,
                                fontWeight: 700,
                                border: `1px solid ${alpha(scoreColor, 0.3)}`
                              }} 
                            />
                          </Box>
                        </Box>
                        
                        <LinearProgress
                          variant="determinate"
                          value={finalScore}
                          sx={{
                            height: 12,
                            borderRadius: 6,
                            backgroundColor: alpha(scoreColor, 0.2),
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 6,
                              background: `linear-gradient(90deg, ${scoreColor}, ${alpha(scoreColor, 0.8)})`,
                            },
                          }}
                        />
                      </Box>
                      
                      {/* Background decoration */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -10,
                          right: -10,
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          background: alpha(scoreColor, 0.1),
                          zIndex: 0
                        }}
                      />
                    </Paper>
                  );
                })()}
                
                <Popover
                  open={openBreakdown && breakdownAgentId === agent.id}
                  anchorEl={breakdownAnchor}
                  onClose={handleCloseBreakdown}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{ 
                    sx: { 
                      p: 3, 
                      borderRadius: 3, 
                      boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                      minWidth: 300
                    } 
                  }}
                >
                  {(() => {
                    const { contributions, maxPossible } = computeFinalAgentScore(agent);
                    const rows = contributions.map(c => ({
                      label: toTitleCase(c.key),
                      value: c.normalizedVal,
                      weight: c.weight,
                      product: c.product
                    }));
                    const total = rows.reduce((s, r) => s + r.product, 0);
                    return (
                      <Box>
                        <Typography variant="h6" fontWeight="700" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Award size={20} />
                          Score Breakdown
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {rows.map((r) => (
                          <Box key={r.label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, p: 1, borderRadius: 1, '&:hover': { bgcolor: 'grey.50' } }}>
                            <Typography variant="body2" fontWeight={500}>{r.label}</Typography>
                            <Typography variant="body2" color="text.secondary">{r.value} × {r.weight.toFixed(1)} = {r.product.toFixed(1)}</Typography>
                          </Box>
                        ))}
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                          <Typography variant="body2" fontWeight={600} color="primary.contrastText">
                            Total: {total.toFixed(1)} / {maxPossible.toFixed(1)} = {Math.round((total / maxPossible) * 100)}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })()}
                </Popover>

                <Typography variant="h6" fontWeight="700" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShieldCheck size={22} />
                  Performance Metrics
                </Typography>

                <Stack spacing={3}>
                  {Object.entries(agent.scores).map(([key, value]) => {
                    // Check if the current score is ROI_per_customer
                    const isROIScore = key === 'ROI_per_customer';
                    const scoreColor = isROIScore ? (normalizeRoiToScore(value) >= 80 ? '#4C84FF' : normalizeRoiToScore(value) >= 60 ? '#FF9F43' : normalizeRoiToScore(value) >= 40 ? '#FF9F43' : '#FF5C75') : getColor(value, key);
                    const isPositive = !invertColors.includes(key);
                    const normalizedScore = isROIScore ? `₹${value.toLocaleString('en-IN')}` : value;
                    const displayValue = isROIScore ? normalizeRoiToScore(value) : value;

                    return (
                      <Paper 
                        key={key}
                        elevation={0}
                        sx={{ 
                          p: 3, 
                          border: '1px solid', 
                          borderColor: 'divider', 
                          borderRadius: 3,
                          background: `linear-gradient(135deg, ${alpha(scoreColor, 0.03)} 0%, transparent 100%)`,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: alpha(scoreColor, 0.3),
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          {/* Left side - Label and Score */}
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                                {toTitleCase(key)}
                              </Typography>
                              <MuiTooltip title={paramDefinitions[key] || 'Definition to be updated'}>
                                <IconButton size="small">
                                  <Info size={14} />
                                </IconButton>
                              </MuiTooltip>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="h5" fontWeight="700" sx={{ color: scoreColor }}>
                                {displayValue}
                              </Typography>
                              {isROIScore ? (
                                <Chip 
                                  size="small" 
                                  label={`Amount: ${normalizedScore}`}
                                  sx={{ bgcolor: alpha(scoreColor, 0.1), color: scoreColor, fontWeight: 600 }}
                                />
                              ) : (
                                <Box sx={{ color: scoreColor }}>
                                  {isPositive ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
                                </Box>
                              )}
                            </Box>
                          </Box>
                          
                          {/* Right side - Weight Control */}
                          <Box sx={{ minWidth: 120, ml: 2 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                              <Settings size={12} />
                              Weight: {(weights[key] ?? 0).toFixed(1)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Slider
                                value={Number(weights[key] ?? 0)}
                                onChange={(_, v) => handleWeightChange(key, Number(v))}
                                min={0}
                                max={1}
                                step={0.1}
                                size="small"
                                sx={{ 
                                  width: 80,
                                  color: scoreColor,
                                  '& .MuiSlider-thumb': {
                                    width: 16,
                                    height: 16,
                                    boxShadow: `0 0 0 4px ${alpha(scoreColor, 0.2)}`
                                  }
                                }}
                              />
                              <Badge 
                                badgeContent={(weights[key] ?? 0).toFixed(1)} 
                                sx={{ 
                                  '& .MuiBadge-badge': { 
                                    bgcolor: scoreColor, 
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.7rem'
                                  } 
                                }}
                              >
                                <Box sx={{ width: 8, height: 8 }} />
                              </Badge>
                            </Box>
                          </Box>
                        </Box>
                        
                        {/* Progress bar for visual representation */}
                        <LinearProgress
                          variant="determinate"
                          value={normalizedScore}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: alpha(scoreColor, 0.2),
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 3,
                              backgroundColor: scoreColor,
                            },
                          }}
                        />
                      </Paper>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default AgentsPage;